import React from "react";

interface PieChartIconProps {
  percentage: number;
  size: number;
}

const BLUE_COLOR = "#66CCFF";
const WHITE_COLOR = "#FFFFFF";

const calculateStrokeWidth = (size: number): number => size * 0.03;

const calculateRadius = (size: number, strokeWidth: number): number =>
  size / 2 - strokeWidth / 2;

const calculateCoordinates = (
  center: number,
  radius: number,
  endAngle: number
): Record<string, number> => ({
  x: center + radius * Math.sin(endAngle),
  y: center - radius * Math.cos(endAngle),
});

export const PieChartIcon: React.FC<PieChartIconProps> = ({
  percentage,
  size,
}: PieChartIconProps): React.JSX.Element | null => {
  if (percentage < 0 || percentage > 100 || size < 0) {
    return null;
  }

  const center = size / 2;
  const strokeWidth = calculateStrokeWidth(size);
  const radius = calculateRadius(size, strokeWidth);
  const endAngle = (percentage / 100) * 2 * Math.PI;
  const { x, y } = calculateCoordinates(center, radius, endAngle);
  const largeArcFlag = percentage > 50 ? 1 : 0;
  const pathDefinition = `M ${center},${center} L ${center},${
    center - radius
  } A ${radius},${radius} 0 ${largeArcFlag},1 ${x},${y} z`;

  return (
    <svg width={size} height={size} data-testid="pie-chart-icon">
      <circle
        fill={percentage === 100 ? BLUE_COLOR : WHITE_COLOR}
        r={radius + strokeWidth / 2}
        cx={center}
        cy={center}
      />
      {percentage < 100 && (
        <path data-testid="blue-path" fill={BLUE_COLOR} d={pathDefinition} />
      )}
      <circle
        data-testid="outline-circle"
        stroke={BLUE_COLOR}
        strokeWidth={strokeWidth}
        fill="transparent"
        r={radius}
        cx={center}
        cy={center}
      />
    </svg>
  );
};
